import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Loader, SectionCard, axisProps, gridProps, tooltipProps } from "@/components/ui";
import { LeaveBalanceGrid } from "@/components/hrms";

const RULES = [
  ["Annual leave", "30 calendar days per year after the eligibility period."],
  ["Sick leave", "Up to 90 days per year — 15 full pay, 30 half pay, 45 unpaid."],
  ["Probation", "No paid sick leave during the probation period."],
  ["Maternity", "60 days — 45 full pay and 15 half pay."],
  ["Compassionate", "5 days on the death of an immediate family member."],
];

export default function LeaveBalance() {
  const { user } = useAuth();
  const { data: bal } = useData(() => api.leaves.balance(user.employee_id), [user.employee_id]);
  if (!bal) return <Loader />;

  const withEntitlement = bal.filter((b) => b.entitled > 0);

  return (
    <>
      <SectionCard title="Balances" subtitle={`Cycle: 1 Jan – 31 Dec ${new Date().getFullYear()}`}>
        <LeaveBalanceGrid balance={withEntitlement} className="lg:grid-cols-4 xl:grid-cols-7" />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SectionCard title="Used vs Remaining" className="lg:col-span-3">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={withEntitlement} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="leave_type" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip {...tooltipProps} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="used" name="Used" fill="#F5A623" radius={[6, 6, 0, 0]} maxBarSize={28} />
              <Bar dataKey="balance" name="Remaining" fill="#225881" radius={[6, 6, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="UAE Leave Rules" subtitle="Per UAE Labour Law" className="lg:col-span-2">
          <ul className="space-y-3">
            {RULES.map(([k, v]) => (
              <li key={k} className="flex gap-2.5 text-xs">
                <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-brand-orange shrink-0" />
                <span><b className="text-brand-blue">{k}.</b> <span className="text-slate-500">{v}</span></span>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </>
  );
}
